import { FC, PropsWithChildren, useEffect, useState } from 'react';
import { useInstantSearch, useSearchBox } from 'react-instantsearch';
import { logger } from '@belong/logging';
import { ISearchErrorBoundary } from './types';

export const SearchErrorBoundary: FC<PropsWithChildren<ISearchErrorBoundary>> = ({
  children,
  ErrorComponent,
  searchInputRef
}) => {
  const { addMiddlewares, status, refresh, error } = useInstantSearch();
  const { refine } = useSearchBox();
  const [searchAPIError, setSearchAPIError] = useState<Error | null>(null);

  const primaryControlAction = {
    onClick: () => {
      refresh();
      if (status !== 'error') {
        setSearchAPIError(null);
        refine(searchInputRef.current?.value || '');
      }
    }
  };

  useEffect(() => {
    if (!error) {
      setSearchAPIError(null);
    }
  }, [error]);

  useEffect(() => {
    const middleware = ({ instantSearchInstance }) => {
      function handleError(searchError) {
        setSearchAPIError(searchError);
        logger.error('Search: ', searchError);
      }

      return {
        subscribe() {
          instantSearchInstance.addListener('error', handleError);
        },
        unsubscribe() {
          instantSearchInstance.removeListener('error', handleError);
        }
      };
    };

    return addMiddlewares(middleware);
  }, [addMiddlewares]);

  if (searchAPIError) {
    return (
      <>
        <ErrorComponent onPrimaryButtonAction={primaryControlAction} />
        <div hidden>{children}</div>
      </>
    );
  }

  return <>{children}</>;
};

SearchErrorBoundary.displayName = 'SearchError';
